import { useEffect, useMemo, useRef, useState } from "react";

function pad2(value) {
  return String(value).padStart(2, "0");
}

function isValidDateParts(year, month, day) {
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day
  );
}

function isoToDisplay(isoValue) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(isoValue || ""));
  if (!match) return "";
  return `${match[3]}-${match[2]}-${match[1]}`;
}

function displayToIso(displayValue) {
  const clean = String(displayValue || "").trim();
  if (!clean) return "";

  const match = /^(\d{2})[/-](\d{2})[/-](\d{4})$/.exec(clean);
  if (!match) return null;

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);

  if (!isValidDateParts(year, month, day)) {
    return null;
  }

  return `${year}-${pad2(month)}-${pad2(day)}`;
}

function DateInputField({
  name,
  value,
  onChange,
  ariaLabel = "Fecha",
  placeholder = "dd-mm-aaaa",
  required = false,
  disabled = false,
}) {
  const nativeInputRef = useRef(null);
  const [displayValue, setDisplayValue] = useState(isoToDisplay(value));
  const normalizedValue = useMemo(() => isoToDisplay(value), [value]);

  useEffect(() => {
    setDisplayValue(normalizedValue);
  }, [normalizedValue]);

  const emitChange = (nextIsoValue) => {
    if (!onChange) return;
    onChange({
      target: {
        name,
        value: nextIsoValue,
      },
    });
  };

  const handleChange = (event) => {
    const nextDisplayValue = event.target.value;
    setDisplayValue(nextDisplayValue);

    const isoValue = displayToIso(nextDisplayValue);
    if (isoValue !== null) {
      emitChange(isoValue);
    }
  };

  const handleBlur = () => {
    const isoValue = displayToIso(displayValue);
    if (isoValue) {
      setDisplayValue(isoToDisplay(isoValue));
      return;
    }
    if (!displayValue.trim()) {
      setDisplayValue("");
    }
  };

  const handleNativeChange = (event) => {
    const isoValue = event.target.value;
    if (!isoValue) {
      setDisplayValue("");
      emitChange("");
      return;
    }

    setDisplayValue(isoToDisplay(isoValue));
    emitChange(isoValue);
  };

  const openNativePicker = () => {
    const input = nativeInputRef.current;
    if (!input || disabled) {
      return;
    }

    if (typeof input.showPicker === "function") {
      input.showPicker();
      return;
    }

    input.focus();
    input.click();
  };

  const handleTextInputClick = (event) => {
    if (disabled) {
      return;
    }

    openNativePicker();

    if (nativeInputRef.current && typeof nativeInputRef.current.showPicker !== "function") {
      nativeInputRef.current.focus();
    }
  };

  return (
    <div className="date-input-row">
      <input
        type="text"
        inputMode="numeric"
        name={name}
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onClick={handleTextInputClick}
        placeholder={placeholder}
        aria-label={ariaLabel}
        required={required}
        disabled={disabled}
        autoComplete="off"
        maxLength={10}
      />
      <input
        ref={nativeInputRef}
        className="date-native-proxy"
        type="date"
        tabIndex={-1}
        aria-hidden="true"
        value={value || ""}
        onChange={handleNativeChange}
        disabled={disabled}
      />
    </div>
  );
}

export default DateInputField;
